import React from 'react';
import { Building, Calendar, CheckCircle, Phone, Mail, MapPin, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

export function ProposalPreview({ proposal, lead, walkthrough, bid, companyInfo }) {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getExpirationDate = () => {
    const created = new Date(proposal.created_at);
    const expiration = new Date(created);
    expiration.setDate(expiration.getDate() + 30);
    return expiration;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-300',
      sent: 'bg-blue-100 text-blue-800 border-blue-300',
      won: 'bg-green-100 text-green-800 border-green-300',
      declined: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || colors.draft;
  };

  // Default cleaning matrix
  const cleaningMatrix = proposal.cleaning_matrix || {
    'Main Entrance / Common Areas / Hallways': [
      { task: 'Empty trash and replace liners', nightly: true, weekly: false, monthly: false },
      { task: 'Vacuum carpets and rugs', nightly: true, weekly: false, monthly: false },
      { task: 'Sweep and mop hard floors', nightly: true, weekly: false, monthly: false },
      { task: 'Dust horizontal surfaces', nightly: false, weekly: true, monthly: false },
      { task: 'Spot clean interior glass', nightly: false, weekly: true, monthly: false },
      { task: 'Dust vents and light fixtures', nightly: false, weekly: false, monthly: true }
    ],
    'Offices / Workspaces / Conference Rooms': [
      { task: 'Empty trash and replace liners', nightly: true, weekly: false, monthly: false },
      { task: 'Vacuum carpeted areas', nightly: true, weekly: false, monthly: false },
      { task: 'Dust desks and surfaces', nightly: false, weekly: true, monthly: false },
      { task: 'Clean whiteboards', nightly: false, weekly: true, monthly: false },
      { task: 'Spot clean walls and switches', nightly: false, weekly: false, monthly: true }
    ],
    'Breakrooms / Kitchen / Lounge': [
      { task: 'Empty trash and recycling', nightly: true, weekly: false, monthly: false },
      { task: 'Clean countertops and tables', nightly: true, weekly: false, monthly: false },
      { task: 'Clean microwave interior', nightly: false, weekly: true, monthly: false },
      { task: 'Clean exterior appliances', nightly: false, weekly: true, monthly: false },
      { task: 'Sweep and mop floors', nightly: true, weekly: false, monthly: false },
      { task: 'Deep clean refrigerator', nightly: false, weekly: false, monthly: true }
    ],
    'Restrooms': [
      { task: 'Empty trash and replace liners', nightly: true, weekly: false, monthly: false },
      { task: 'Clean and disinfect toilets', nightly: true, weekly: false, monthly: false },
      { task: 'Clean and disinfect sinks', nightly: true, weekly: false, monthly: false },
      { task: 'Restock paper and soap', nightly: true, weekly: false, monthly: false },
      { task: 'Sweep and mop floors', nightly: true, weekly: false, monthly: false },
      { task: 'Clean mirrors and fixtures', nightly: true, weekly: false, monthly: false },
      { task: 'Deep clean tile and grout', nightly: false, weekly: false, monthly: true }
    ]
  };

  return (
    <div className="proposal-preview bg-white max-w-5xl mx-auto p-8 space-y-8" data-testid="proposal-preview">
      {/* 1. COVER / SUMMARY HEADER */}
      <div className="space-y-6 border-b-2 border-teal-600 pb-8">
        <div className="flex justify-between items-start">
          <div>
            {companyInfo?.logo && (
              <img src={companyInfo.logo} alt="Company Logo" className="h-16 mb-4" />
            )}
            <h1 className="text-4xl font-[Space_Grotesk] font-bold text-gray-900">
              {companyInfo?.company_name || 'Tidify Cleaning Services'}
            </h1>
            <p className="text-lg text-teal-600 font-medium mt-1">Janitorial Services Proposal</p>
          </div>
          <div className="text-right">
            <Badge className={`${getStatusColor(proposal.status)} mb-2`}>
              {proposal.status.toUpperCase()}
            </Badge>
            <p className="text-sm text-gray-600">Proposal ID: {proposal.id?.slice(0, 8)}</p>
            <p className="text-sm text-gray-600">Date: {formatDate(proposal.created_at)}</p>
            <p className="text-sm text-gray-600">Valid Until: {formatDate(getExpirationDate())}</p>
          </div>
        </div>

        {/* Client Info & Summary Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <Card className="md:col-span-2 border-teal-200">
            <CardHeader className="bg-teal-50">
              <CardTitle className="text-xl flex items-center gap-2">
                <Building className="h-5 w-5 text-teal-600" />
                Prepared For
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-2xl font-semibold text-gray-900">{proposal.company_name || lead?.company_name}</p>
                <p className="text-gray-600">{lead?.contact_name}</p>
                {lead?.address && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4" />
                    {lead.address}
                  </p>
                )}
                {lead?.phone && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-4 w-4" />
                    {lead.phone}
                  </p>
                )}
                {lead?.email && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <Mail className="h-4 w-4" />
                    {lead.email}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-teal-600 border-2 bg-teal-50">
            <CardHeader>
              <CardTitle className="text-teal-900">Investment Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <p className="text-sm text-teal-700 mb-1">Monthly Service Fee</p>
                <p className="text-4xl font-bold text-teal-900 tabular-nums">${proposal.monthly_price}</p>
                <p className="text-xs text-teal-700 mt-2">{walkthrough?.frequency?.replace('_', ' ') || 'Weekly'} Service</p>
                <p className="text-xs text-teal-700">{walkthrough?.square_footage || 'N/A'} sq ft</p>
                <p className="text-xs text-teal-700">{walkthrough?.building_type?.replace('_', ' ') || 'Commercial'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 2. EXECUTIVE SUMMARY */}
      <div className="space-y-4">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold text-gray-900 border-b border-gray-300 pb-2">
          Executive Summary
        </h2>
        <div className="bg-gray-50 p-6 rounded-lg">
          <p className="text-gray-700 leading-relaxed">
            {proposal.executive_summary || `Thank you for the opportunity to provide janitorial services for ${proposal.company_name || 'your facility'}. We are committed to delivering consistent, high-quality cleaning services with professional supervision, regular inspections, and responsive communication. Our team ensures your facility maintains a clean, healthy, and welcoming environment for your staff and visitors.`}
          </p>
        </div>
      </div>

      {/* 3. FACILITY SCOPE SNAPSHOT */}
      <div className="space-y-4">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold text-gray-900 border-b border-gray-300 pb-2">
          Facility Overview
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Square Footage</p>
              <p className="text-2xl font-bold text-gray-900">{walkthrough?.square_footage || 'N/A'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Building Type</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">{walkthrough?.building_type?.replace('_', ' ') || 'N/A'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Frequency</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">{walkthrough?.frequency?.replace('_', ' ') || 'N/A'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Condition</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">{walkthrough?.condition || 'N/A'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Restrooms</p>
              <p className="text-2xl font-bold text-gray-900">{walkthrough?.num_restrooms || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Breakrooms</p>
              <p className="text-2xl font-bold text-gray-900">{walkthrough?.num_breakrooms || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Offices</p>
              <p className="text-2xl font-bold text-gray-900">{walkthrough?.num_offices || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Floor Types</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">{walkthrough?.floor_types?.replace('_', ' ') || 'N/A'}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 4. CLEANING SPECIFICATIONS MATRIX */}
      <div className="space-y-4">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold text-gray-900 border-b border-gray-300 pb-2">
          Cleaning Specifications
        </h2>
        <div className="space-y-6">
          {Object.entries(cleaningMatrix).map(([area, tasks]) => (
            <div key={area}>
              <h3 className="text-lg font-semibold text-teal-700 mb-3 bg-teal-50 p-2 rounded">{area}</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">Task</th>
                      <th className="text-center p-3 text-sm font-semibold text-gray-700 w-24">Nightly</th>
                      <th className="text-center p-3 text-sm font-semibold text-gray-700 w-24">Weekly</th>
                      <th className="text-center p-3 text-sm font-semibold text-gray-700 w-24">Monthly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task, idx) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="p-3 text-sm text-gray-800">{task.task}</td>
                        <td className="p-3 text-center">
                          {task.nightly && <CheckCircle className="h-5 w-5 text-teal-600 mx-auto" />}
                        </td>
                        <td className="p-3 text-center">
                          {task.weekly && <CheckCircle className="h-5 w-5 text-teal-600 mx-auto" />}
                        </td>
                        <td className="p-3 text-center">
                          {task.monthly && <CheckCircle className="h-5 w-5 text-teal-600 mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 5. INVESTMENT SECTION */}
      <div className="space-y-4">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold text-gray-900 border-b border-gray-300 pb-2">
          Investment
        </h2>
        <Card className="border-teal-200">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-4 border-b-2 border-teal-600">
                <span className="text-lg font-semibold text-gray-900">Monthly Service Fee</span>
                <span className="text-3xl font-bold text-teal-700 tabular-nums">${proposal.monthly_price}</span>
              </div>
              {proposal.notes && (
                <div className="pt-2">
                  <p className="text-sm font-semibold text-gray-700 mb-1">Notes:</p>
                  <p className="text-sm text-gray-600">{proposal.notes}</p>
                </div>
              )}
              <div className="pt-2 text-sm text-gray-600">
                <p>• Billing: Monthly in advance</p>
                <p>• Payment terms: Net 15 days</p>
                {walkthrough?.supplies_included && <p>• Supplies: Included in monthly fee</p>}
                {!walkthrough?.supplies_included && <p>• Supplies: Provided by facility</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 6. TERMS / ASSUMPTIONS / EXCLUSIONS */}
      <div className="space-y-4">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold text-gray-900 border-b border-gray-300 pb-2">
          Terms & Conditions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-teal-700">Included Services</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>• All tasks listed in Cleaning Specifications</li>
                <li>• Professional supervision and quality control</li>
                <li>• Regular inspections</li>
                <li>• Responsive communication</li>
                {walkthrough?.supplies_included && <li>• Cleaning supplies and consumables</li>}
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-gray-700">Exclusions</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>• {proposal.exclusions || 'Window washing (exterior)'}</li>
                <li>• Floor stripping and waxing (quoted separately)</li>
                <li>• Carpet extraction (quoted separately)</li>
                <li>• Pressure washing</li>
                <li>• Special event cleaning</li>
              </ul>
            </CardContent>
          </Card>
        </div>
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <p className="text-sm text-gray-700"><strong>Service Term:</strong> Month-to-month with 30-day cancellation notice</p>
            <p className="text-sm text-gray-700 mt-2"><strong>Insurance:</strong> Fully insured with General Liability and Workers' Compensation coverage</p>
          </CardContent>
        </Card>
      </div>

      {/* 7. WHY CHOOSE US */}
      <div className="space-y-4">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold text-gray-900 border-b border-gray-300 pb-2">
          Why Choose {companyInfo?.company_name || 'Us'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-teal-200">
            <CardContent className="p-6 text-center">
              <CheckCircle className="h-12 w-12 text-teal-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Quality Assurance</h3>
              <p className="text-sm text-gray-600">Regular inspections and quality control ensure consistent results</p>
            </CardContent>
          </Card>
          <Card className="border-teal-200">
            <CardContent className="p-6 text-center">
              <FileText className="h-12 w-12 text-teal-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Professional Team</h3>
              <p className="text-sm text-gray-600">Trained, background-checked, and reliable cleaning professionals</p>
            </CardContent>
          </Card>
          <Card className="border-teal-200">
            <CardContent className="p-6 text-center">
              <Phone className="h-12 w-12 text-teal-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Responsive Support</h3>
              <p className="text-sm text-gray-600">Direct communication and quick response to your needs</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 8. ACCEPTANCE SECTION */}
      <div className="space-y-4 border-t-2 border-gray-300 pt-8">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold text-gray-900">
          Proposal Acceptance
        </h2>
        <Card className="bg-teal-50 border-teal-300">
          <CardContent className="p-6">
            <p className="text-sm text-gray-700 mb-6">
              By signing below, you agree to the services, pricing, and terms outlined in this proposal. 
              We appreciate your business and look forward to serving you.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Client Signature:</p>
                  <div className="border-b-2 border-gray-400 pb-8"></div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Printed Name: ______________________</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Title: ______________________</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date: ______________________</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Company Representative:</p>
                  <div className="border-b-2 border-gray-400 pb-8"></div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Name: {companyInfo?.representative_name || '______________________'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Title: {companyInfo?.representative_title || '______________________'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Projected Start Date: ______________________</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500 pt-8 border-t border-gray-200">
        <p>{companyInfo?.company_name || 'Tidify Cleaning Services'}</p>
        {companyInfo?.address && <p>{companyInfo.address}</p>}
        <p>
          {companyInfo?.phone && <span>{companyInfo.phone} • </span>}
          {companyInfo?.email && <span>{companyInfo.email} • </span>}
          {companyInfo?.website && <span>{companyInfo.website}</span>}
        </p>
      </div>
    </div>
  );
}
